import {defineStore} from "pinia";
import {type Ref, ref} from "vue";
import {Source} from "@/domain/type/source.type.ts";
import type {SynchroFormData, SynchroHistory} from "@/domain/type/synchro.type.ts";
import {SynchroType} from "@/domain/type/synchro.type.ts";
import SynchroApi from "@/api/synchro.api.ts";
import type {BlockDetails, SynchroRequestField, SynchroRequestFieldList} from "@/domain/type/synchro.api.type.ts";
import {RequestEntity} from "@/domain/type/synchro.api.type.ts";

export const useSynchroStore = defineStore('synchro', () => {
  const sourceLeft: Ref<Source> = ref(Source.EVA)
  const sourceRight: Ref<Source> = ref(Source.AMMON)
  const src1History: Ref<SynchroHistory[]> = ref([])
  const src2History: Ref<SynchroHistory[]> = ref([])
  const details: Ref<BlockDetails | null> = ref(null)
  const groupUid: Ref<string> = ref('')
  const requestList: Ref<SynchroRequestFieldList> = ref({})
  const requestListOrder: Ref<number[]> = ref([])
  const id = ref('')
  const isFetching = ref(false)
  const form: Ref<SynchroFormData> = ref({})
  const type: Ref<SynchroType> = ref(SynchroType.COMPANIES)
  const entityUuid = ref('')
  const entity: Ref<null | RequestEntity> = ref(null)
  const error: Ref<string | null> = ref(null);

  function cleanValue(value: string | null | undefined): string {
    return (value ?? '').replace(/&nbsp;/gi, ' ')
  }

  async function submit() {
    isFetching.value = true
    return SynchroApi.applyEntity(entityUuid.value, form.value)
      .then(() => {
        fetch().finally(() => {
          isFetching.value = false
        })
      })
  }

  function reset() {
    details.value = null;
    requestList.value = {}
    form.value = {}
    error.value = null
  }

  function getSrc1Link(): string {
    return (entity.value?.entity.find((detail) => detail.label === 'Lien')?.val) as string
  }

  function getSrc2Link(): string {
    return (entity.value?.matchingEntities[0]?.find((detail) => detail.label === 'Lien')?.val ?? '') as string
  }

  function fetchHistory() {
    SynchroApi.getHistorySrc1(groupUid.value).then((response) => {
      src1History.value = response.data.block.history
    })
    SynchroApi.getHistorySrc2(groupUid.value).then((response) => {
      src2History.value = response.data.block.history
    })
  }

  async function fetch() {
    isFetching.value = true
    return SynchroApi.demand(sourceLeft.value, sourceRight.value, type.value, id.value).then(
      (demandResponse) => {
        if (demandResponse.data.error) {
          isFetching.value = false
          switch (demandResponse.data.error) {
            case 'Not diffusable Establishment':
              error.value = 'Cette entreprise n\'autorise pas la diffusion de ses données.'
              break;
            default:
              error.value = demandResponse.data.error
              break;
          }
          return Promise.resolve()
        }
        if (demandResponse.data.requestUuid) {
          return SynchroApi.getRequest(demandResponse.data.requestUuid).then((getRequestResponse) => {
            if (undefined === getRequestResponse.data.entities[0]) {
              reset()
              return Promise.resolve().finally(() => {
                isFetching.value = false
              })
            }
            entity.value = new RequestEntity(getRequestResponse.data.entities[0])
            entityUuid.value = getRequestResponse.data.entities[0].uuid
            return SynchroApi.getEntityFields(entityUuid.value).then((getResponse) => {
              reset()

              const newSrcLeft = getResponse.data.src1.toLowerCase()[0] + getResponse.data.src1.slice(1) as Source
              const newSrcRight = getResponse.data.src2.toLowerCase()[0] + getResponse.data.src2.slice(1) as Source

              details.value = getResponse.data.block.details
              sourceLeft.value = newSrcLeft
              sourceRight.value = newSrcRight
              groupUid.value = getResponse.data.block.groupUid
              for (const [key, value] of Object.entries<SynchroRequestField>(getResponse.data.block.requestList)) {
                requestList.value[key] = {
                  isDisallowed: value.isDisallowed,
                  isOptionnel: value.isOptionnel,
                  isSynchronized: value.isSynchronized,
                  isSensitive: value.isSensitive,
                  readOnlySrc1Info: value.readOnlySrc1Info,
                  readOnlySrc2Info: value.readOnlySrc2Info,
                  label: value.label,
                  readOnlySrc1: value.readOnlySrc1,
                  readOnlySrc2: value.readOnlySrc2,
                  synchroRequestId: value.synchroRequestId,
                  synchroStatus: value.synchroStatus,
                  valueSrc1: cleanValue(value.valueSrc1),
                  valueSrc2: cleanValue(value.valueSrc2),
                  displayValueSrc1: null === value.displayValueSrc1 || undefined === value.displayValueSrc1 ? undefined : cleanValue(value.displayValueSrc1),
                  displayValueSrc2: null === value.displayValueSrc2 || undefined === value.displayValueSrc2 ? undefined : cleanValue(value.displayValueSrc2),
                }
              }

              fetchHistory()

              return Promise.resolve().finally(() => {
                isFetching.value = false
              })
            })
          })
        }
      });
  }

  return {
    details,
    error,
    fetch,
    fetchHistory,
    form,
    groupUid,
    id,
    isFetching,
    reset,
    requestList,
    requestListOrder,
    sourceLeft,
    sourceRight,
    src1History,
    src2History,
    submit,
    type,
    entityUuid,
    entity,
    getSrc1Link,
    getSrc2Link,
  }
})
